<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settlements.settlements') }}</span>
        <h3 class="page-title">{{ $t('settlements.invoiceList') }}</h3>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="settlements-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <date-range-picker
          ref="picker"
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
            'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
          }"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="date_range"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 350px;">
            {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
          </template>
        </date-range-picker>

        <v-select
          style="width: 300px;"
          class="ml-2"
          :options="paidStatusOptionsForSelect"
          v-model="filters.status"
          :placeholder="$t('ui.isSettled')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          :class="{
            'text-nowrap': true,
            'ml-auto': true
          }"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.invoiceNumber') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('settlements.type') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('settlements.amount') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('settlements.deadlines') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('status.status') }}</th>
            <th scope="col" class="border-0" style="width: 100px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(settlement, index) of settlements"
            :key="'settlement-' + index">
            <td class="align-middle">
              {{ settlement.number }}
            </td>
            <td class="align-middle">
              {{ settlement.type }}
            </td>
            <td class="align-middle">
              {{ Number(settlement.amount).toFixed(2) }} {{ settlement.currency }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('settlements.issued') }}: <strong>{{ settlement.invoice_date }}</strong><br>
                {{ $t('settlements.paymentDueDate') }}: <strong>{{ settlement.payment_date }}</strong><br>
                {{ $t('ui.settled') }}: <strong>{{ settlement.settlement_date }}</strong>
              </small>
            </td>
            <td class="align-middle">
              <span :class="paidStatusOptions[settlement.status].cssClass">
                {{ paidStatusOptions[settlement.status].label }}
              </span>
            </td>
            <td class="align-middle">
              <d-button
                theme="info"
                :disabled="!!downloading"
                @click.prevent="download(settlement.id, settlement.number, settlement.order.order_name)">
                <template v-if="downloading !== settlement.id"><i class="material-icons">cloud_download</i></template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </td>
          </tr>
          <tr v-if="settlements.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('settlements.noSettlementsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'settlements',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination
  },
  computed: {
    paidStatusOptions () {
      return {
        'PAID': {
          label: this.$t('ui.settled'),
          cssClass: 'text-success'
        },
        'UNPAID': {
          label: this.$t('ui.notSettled'),
          cssClass: 'text-danger'
        }
      };
    },
    paidStatusOptionsForSelect () {
      return [
        {
          text: this.$t('ui.settled'),
          value: 'PAID'
        },
        {
          text: this.$t('ui.notSettled'),
          value: 'UNPAID'
        }
      ];
    }
  },
  data() {
    return {
      settlements: [],
      loaded: false,
      loadError: false,
      downloading: false,
      downloadingAllInvoices: false,
      filters: {
        status: null
      },
      date_range: {
        startDate: null,
        endDate: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};

      if (this.date_range.startDate !== null) {
        whereData.invoice_date_from = this.prepareDateString(this.date_range.startDate);
      }

      if (this.date_range.endDate !== null) {
        whereData.invoice_date_to = this.prepareDateString(this.date_range.endDate);
      }

      if (this.filters.status !== null) {
        whereData.status = this.filters.status.value;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/customer-panel/settlements/items?page=' + this.currentPage,
        listField: 'settlements',
        loadError: 'loadError',
        params: {
          with: ['order'],
          where: whereData
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        invoice_date_from: null,
        invoice_date_to: null,
        status: null
      };
      this.date_range = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['settlements-filters-form'], this.debouncedLoadFilteredData);
    },
    download (settlementID, invoiceNumber, orderName) {
      this.downloading = settlementID;

      this.$http.post('/customer-panel/settlements/download-file/' + settlementID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let fileName = invoiceNumber + '_' + orderName + '.pdf';
        fileName = fileName.replace(/[\s\/\\]/gmi, '-', fileName);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloading = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloading = false;
      });
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  }
};
</script>

